<script>
import { nextTick } from "vue";
import Breadcrumbs from "../components/global/breadcrumbs.vue";
import EssaysTable from "../components/essay/essays_table.vue";
import MoveEssayModal from "../components/essay/modals/move_essay_modal.vue";
import { getClass, deleteClass, deleteEssay } from "../api";
import formatDate from "../utils/formatDate";
import fetchEssays from "@/mixins/fetchEssays";
import i18n from "@/i18n/i18n";

function getTableFields() {
  return [
    {
      key: "status",
      label: i18n.t("common.status"),
      columnClass: "w-25",
    },
    {
      key: "name",
      label: i18n.t("common.assignment"),
      tdClass: "text-truncate",
      columnClass: "w-25",
    },
    {
      key: "createdAt",
      label: i18n.t("essays.gradedOn"),
      columnClass: "w-25",
      formatter: (value) => formatDate(value),
    },
    {
      key: "actions",
      label: "",
      tdClass: "text-right",
      columnClass: "w-25",
    },
  ];
}

export default {
  name: "ClassView",
  components: {
    Breadcrumbs,
    EssaysTable,
    MoveEssayModal,
  },
  mixins: [fetchEssays],

  data() {
    return {
      loading: true,
      error: "",
      name: "",
      size: "",
      description: "",
      createdDate: "",
      currentPage: 1,
      canDelete: false,
      classId: null,
      essayToMoveId: null,
      selectedAll: false,
    };
  },
  computed: {
    breadcrumbs() {
      return [
        {
          text: this.$t("classes.classes"),
          to: "/classes",
        },
        {
          text: this.name || this.$t("classes.viewClass"),
          active: true,
        },
      ];
    },
  },

  watch: {
    essays() {
      this.canDelete = this.essays.length === 0;
    },
  },
  async mounted() {
    // filter essays by class id as default
    this.defaultFilters = { ClassId: this.$route.params.id };
    this.fetchClassWithEssays();
  },

  methods: {
    getTableFields,
    async fetchClassWithEssays() {
      await nextTick();

      this.loading = true;

      try {
        const { data } = await getClass(this.$route.params.id);
        await this.fetchEssays(); // automatically fetches essays and essays this.limit, this.count, this.queuedCount

        this.name = data.classData.name;
        this.description = data.classData.description;
        this.size = data.classData.studentsCount ?? data.classData.size ?? 0;
        this.createdDate = formatDate(data.classData.createdAt);
        this.classId = data.classData.id;

        this.loading = false;
        this.error = "";
      } catch (error) {
        this.loading = false;
        this.error = error.response?.data?.error;
      }
    },
    gradedDate(date) {
      return formatDate(date);
    },
    async deleteSingleClass() {
      try {
        this.loading = true;

        await deleteClass(this.classId);

        this.$router.push("/classes?deleted=true");

        this.loading = false;
        this.error = "";
      } catch (error) {
        this.loading = false;
        this.error = error.response?.data?.error;
      }
    },
    showMoveEssayModal(essayId) {
      this.essayToMoveId = essayId;
      this.$bvModal.show("move-essay-modal");
    },
    rerouteToClasses() {
      this.$router.push("/classes");
    },
    setMoveError(error) {
      this.error = error;
    },
    async deleteSingleEssay(essayId) {
      try {
        this.loading = true;

        await deleteEssay(essayId);

        await this.fetchClassWithEssays();

        this.showDeleteSuccess();
      } catch (error) {
        this.loading = false;
        this.error = error.response?.data?.error;
      }
    },
    showDeleteSuccess() {
      this.$showToastSuccess(this.$t("classes.essaysDeletedSuccessfully"), {
        title: this.$t("classes.essaysDeleted"),
        autoHideDelay: 3000,
      });
    },
  },
};
</script>

<template>
  <eg-b-container>
    <section v-if="loading || error">
      <eg-b-row>
        <eg-b-col>
          <div v-if="loading" class="text-center">
            <eg-b-spinner class="mb-3"></eg-b-spinner>
            <p>{{ $t("common.processingRequest") }}</p>
          </div>
          <eg-b-alert v-else variant="danger" show>{{ error }}</eg-b-alert>
        </eg-b-col>
      </eg-b-row>
    </section>
    <section v-else>
      <breadcrumbs :items="breadcrumbs" />
      <eg-b-row>
        <eg-b-col lg="12" md="8">
          <div class="d-flex justify-content-between">
            <div>
              <h1 class="mb-0">{{ name }}</h1>
              <eg-b-badge class="mr-2" pill variant="grey">
                <eg-b-icon icon="people"></eg-b-icon>
                {{ $t("classes.students", { count: size }) }}
              </eg-b-badge>
              <eg-b-badge class="mr-2" pill variant="grey">
                <eg-b-icon icon="file-text"></eg-b-icon>
                {{ $t("classes.essays", { count: count }) }}
              </eg-b-badge>
              <span class="light-grey-text"
                >{{ $t("rubrics.createdOn") }} {{ createdDate }}</span
              >
              <p class="mt-3 my-3 class-description">
                {{ description }}
              </p>
            </div>

            <div>
              <eg-b-button :to="`/class/edit/${classId}`" class="mr-2">
                {{ $t("common.edit") }}
              </eg-b-button>
              <eg-b-button
                variant="danger"
                :disabled="!canDelete"
                @click="deleteSingleClass"
              >
                {{ $t("common.delete") }}
              </eg-b-button>
              <eg-b-icon
                v-if="!canDelete"
                v-b-popover.hover.top="$t('classes.classesCanOnlyBeDeleted')"
                class="ml-2 question-icon"
                icon="question-circle"
              ></eg-b-icon>
            </div>
          </div>
          <div v-if="essays.length > 0 || isFiltered">
            <essays-table
              :essays="essays"
              :fields="getTableFields()"
              :available-filters="['name', 'rubric']"
              :sort-by="sortBy"
              :sort-desc="sortDesc"
              :table-loading="tableLoading"
              :total="+count"
              :limit="+limit"
              :current-page="+currentPage"
              :selected-all="selectedAll"
              @selectedAll="selectedAll = $event"
              @onChangePage="(currentPage = $event) && fetchEssays()"
              @move="showMoveEssayModal"
              @delete="deleteSingleEssay"
              @getData="fetchEssays"
            />
          </div>

          <div v-else class="essay-box-empty">
            <h3>{{ $t("classes.empty") }}</h3>
            <p>{{ $t("classes.noEssays") }}</p>
            <eg-b-button variant="info" to="/">{{
              $t("classes.gradeNewEssay")
            }}</eg-b-button>
          </div>
        </eg-b-col>
      </eg-b-row>
    </section>

    <move-essay-modal
      :essay-id="essayToMoveId"
      @essayMoved="rerouteToClasses"
      @moveError="setMoveError"
    />
  </eg-b-container>
</template>
